import { useEffect } from 'react';
import {
  ThemeProvider,
  StyledEngineProvider,
  useTheme,
} from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { Container, Typography, Link, useMediaQuery } from '@mui/material';
import { useSettings, useUsingVagoSettings } from '../../hooks';
import { useUsingWalnutSettings } from '../../WhiteLabelSettings/Walnut/useUsingWalnutSettings';
import { SaveQuoteButton } from './SaveQuoteButton/SaveQuoteButton';
import { navigate } from '@reach/router';
import { useIsManager } from '../../hooks';
import { styled } from '@mui/material';
import { setDocumentTitle, setDocumentFavicon } from '../../util';

// types and classes
import type { $TSFixMe } from '@calefy-inc/utilityTypes';

const useStyle = () => {
  const onWalnut = useUsingWalnutSettings();
  const onVago = useUsingVagoSettings();
  const styles = makeStyles((theme: $TSFixMe) => ({
    wizard: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      justifyContent: 'space-between',
      overflowX: 'hidden',
      /* backgroundColor: (isManager) => !isManager ? theme.palette.background.neutral : theme.palette.background.neutral, */
    },
    header: {
      position: 'sticky',
      top: 0,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      maxHeight: onVago ? theme.spacing(8) : theme.spacing(12), // Reduced for VaGo
      // margin: `0 ${theme.spacing(6)}`,
      margin: 0,
      padding: onVago ? theme.spacing(4) : theme.spacing(8), // Reduced from spacing(8) to spacing(2) for VaGo
      '& img': {
        maxWidth: onVago ? theme.spacing(25) : theme.spacing(37.5), // Smaller logo for VaGo
        maxHeight: theme.spacing(10), // Constrain height so tall/square logos don't overflow the header
        alignSelf: 'center',
        objectFit: 'contain',
        [theme.breakpoints.down('xl')]: {
          maxWidth: onVago ? theme.spacing(20) : theme.spacing(27.5), // Smaller logo for VaGo
        },
      },
      '& button': {
        alignSelf: 'center',
      },
      [theme.breakpoints.down('xl')]: {
        maxHeight: onVago ? theme.spacing(6) : theme.spacing(8), // Reduced for VaGo
        margin: 0,
      },
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'space-between',
        padding: onVago ? theme.spacing(1.5) : theme.spacing(8), // Reduced padding for VaGo on mobile
      },
    },
    container: {
      flex: 1,
      minHeight: onWalnut ? '80vh' : onVago ? '50vh' : '60vh', // Reduced minHeight for VaGo
    },
    speedDial: {
      position: 'absolute',
      bottom: onVago ? theme.spacing(2) : theme.spacing(4), // Reduced spacing for VaGo
      right: onVago ? theme.spacing(2) : theme.spacing(4), // Reduced spacing for VaGo
    },
    calefy: {
      alignSelf: 'center',
      paddingBottom: onVago ? theme.spacing(0.5) : theme.spacing(1), // Reduced padding for VaGo
    },
  }));
  return styles();
};

/**
 * A Component which renders a quote wizard with specific theme and settings
 * @param  children - child that is wrapped by this component
 * @returns Quote Wizard
 */
const WhiteLabel = ({ children }: { children: $TSFixMe }) => {
  const { isManager } = useIsManager();
  const {
    ready,
    settings: { companyTheme, logo, headerLogo, title, favicon, ...settings },
  } = useSettings();
  const headerLogoToUse = headerLogo ?? logo;
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const classes = useStyle();
  const canRenderContent = isManager || ready;

  useEffect(() => {
    if (isManager || !ready) return;
    setDocumentTitle(title);
    if (!favicon) return;
    setDocumentFavicon(favicon);
  }, [isManager, ready, title, favicon]);
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={!isManager ? companyTheme : theme}>
        <div className={classes.wizard}>
          <StyledHeader
            className={classes.header}
            sx={settings.headerCss ? settings.headerCss : undefined}
          >
            {!isManager && ready && headerLogoToUse ? (
              settings.slug === 'walnut-atb' ? (
                <img
                  src={headerLogoToUse}
                  alt='Logo'
                  style={{ height: '125px' }}
                />
              ) : (
                <img src={headerLogoToUse} alt='Logo' />
              )
            ) : null}
            {matches ? (
              <div style={{ paddingTop: theme.spacing(2) }}></div>
            ) : null}
            {canRenderContent ? <SaveQuoteButton /> : null}
          </StyledHeader>
          <Container fixed className={classes.container}>
            {canRenderContent ? children : null}
          </Container>
          {!canRenderContent ? null : settings.Footer ? (
            <settings.Footer />
          ) : (
            <div className={classes.calefy}>
              <Typography
                variant='caption'
                color='textSecondary'
                style={{ fontWeight: 600 }}
              >
                {!isManager ? (
                  <Link
                    color='inherit'
                    underline='none'
                    onClick={() => navigate('/insurtech/manager/dashboard')}
                  >
                    Powered by
                  </Link>
                ) : (
                  'Powered by'
                )}{' '}
                <Link
                  color='primary'
                  underline='none'
                  href='https://calefy.ca/'
                  target='_blank'
                  rel='noopener'
                >
                  Calefy
                </Link>
              </Typography>
            </div>
          )}
        </div>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default WhiteLabel;

const StyledHeader = styled('header')({});
